// <Dialog> — modal dialog primitive.
//
// Implements: focus trap; ESC to close; backdrop click closes;
// `role="dialog"` + `aria-modal="true"`; auto-focus first focusable on open;
// returns focus to opener on close. Per DESIGN_POLICY.md §5 + a11y-plan.md
// §M.1 (modal pattern).

import {
  useEffect,
  useId,
  useRef,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { tokens } from '../tokens';
import { useReducedMotion } from '../hooks';

export interface DialogAction {
  label: string;
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  onClick(): void;
  /** When true, focused on open (default Cancel for destructive flows). */
  defaultFocus?: boolean;
}

export interface DialogProps {
  /** Required title; rendered visibly + used as the dialog accessible name. */
  title: string;
  /** Required body content. */
  children: ReactNode;
  /** Action footer; left=secondary, right=primary by convention. */
  actions?: DialogAction[];
  /** Closer; called on backdrop click + ESC. */
  onClose(): void;
  /** Optional sizing hint. */
  size?: 'sm' | 'md' | 'lg';
  /** Override `role="dialog"`; e.g. "alertdialog" for ConfirmDialog. */
  role?: 'dialog' | 'alertdialog';
  /** Skip backdrop close (e.g. typed-confirmation flows). */
  blockBackdropClose?: boolean;
  className?: string;
  'data-testid'?: string;
}

const SIZE_TO_WIDTH: Record<NonNullable<DialogProps['size']>, number> = {
  sm: tokens.layout.popoverMd,
  md: tokens.layout.popoverLg,
  lg: tokens.layout.standaloneStateWide,
};

export function Dialog({
  title,
  children,
  actions,
  onClose,
  size = 'md',
  role = 'dialog',
  blockBackdropClose = false,
  className,
  'data-testid': testId,
}: DialogProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const reduced = useReducedMotion();
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    openerRef.current = (document.activeElement as HTMLElement) ?? null;
    const node = dialogRef.current;
    if (!node) return;
    // Focus the default-focused action if present, else first focusable.
    const explicit = node.querySelector<HTMLElement>('[data-default-focus="true"]');
    if (explicit) {
      explicit.focus();
    } else {
      const focusable = node.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      if (!node) return;
      // Focus trap
      const focusables = Array.from(
        node.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      openerRef.current?.focus?.();
    };
  }, [onClose]);

  const portalReady = typeof document !== 'undefined';
  if (!portalReady) return null;

  return createPortal(
    <div
      aria-hidden="false"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: tokens.zIndex.modal,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (blockBackdropClose) return;
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: tokens.color.overlay.scrim,
        }}
      />
      <div
        ref={dialogRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={testId}
        className={className}
        style={{
          position: 'relative',
          width: SIZE_TO_WIDTH[size],
          maxWidth: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: tokens.color.surface[1],
          color: tokens.color.ink.primary,
          borderRadius: tokens.radius.lg,
          boxShadow: tokens.elevation[3],
          padding: tokens.space[5],
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.space[3],
          transitionProperty: 'transform, opacity',
          transitionDuration: `${reduced ? 0 : tokens.motion.duration.normal}ms`,
          transitionTimingFunction: tokens.motion.easing.decel,
        }}
      >
        <h2
          id={titleId}
          style={{
            margin: 0,
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.size.xl,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
          }}
        >
          {title}
        </h2>
        <div style={{ flex: '1 1 auto', minHeight: 0 }}>{children}</div>
        {actions && actions.length > 0 ? (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: tokens.space[2] }}>
            {actions.map((a, idx) => {
              const variantBg =
                a.variant === 'primary'
                  ? tokens.color.accent.primary
                  : a.variant === 'danger'
                    ? tokens.color.semantic.danger
                    : 'transparent';
              const variantFg =
                a.variant === 'primary' || a.variant === 'danger'
                  ? tokens.color.accent.on
                  : tokens.color.ink.primary;
              return (
                <button
                  key={`${a.label}-${idx}`}
                  type="button"
                  data-default-focus={a.defaultFocus ? 'true' : undefined}
                  onClick={a.onClick}
                  style={{
                    appearance: 'none',
                    background: variantBg,
                    color: variantFg,
                    border: a.variant === 'primary' || a.variant === 'danger'
                      ? '1px solid transparent'
                      : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
                    borderRadius: tokens.radius.input,
                    padding: `${tokens.space[2]}px ${tokens.space[4]}px`,
                    fontFamily: tokens.font.family.ui,
                    fontSize: tokens.font.role.body.size,
                    fontWeight: a.variant === 'primary' ? tokens.font.weight.medium : tokens.font.weight.regular,
                    cursor: 'pointer',
                    minHeight: tokens.density.touchMin,
                  }}
                >
                  {a.label}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
